import ContentIllustration from '../../components/ui/ContentIllustration';
import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Navigate, Link } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import api from '../../services/api';
import { Loader2, CheckCircle, AlertTriangle } from 'lucide-react';

export default function RedeemCode() {
    const { user } = useAuthStore();
    const [searchParams] = useSearchParams();
    const code = searchParams.get('code') || '';
    const invitedEmail = searchParams.get('email') || '';
    const [status, setStatus] = useState('loading'); // loading | success | error | no-code
    const [message, setMessage] = useState('');
    const [accessData, setAccessData] = useState(null);
    const redeemed = useRef(false);

    useEffect(() => {
        if (!user || !code) {
            if (!code) setStatus('no-code');
            return;
        }
        if (redeemed.current) return;
        redeemed.current = true;

        const redeem = async () => {
            try {
                const { data } = await api.post('/share/codes.php', {
                    action: 'redeem',
                    code: code.trim(),
                });
                if (data.success) {
                    setStatus('success');
                    setAccessData(data.data);
                    setMessage(`Accès activé pour « ${data.data.scope_label} » jusqu'au ${new Date(data.data.access_end).toLocaleDateString('fr-FR')}`);
                }
            } catch (e) {
                setStatus('error');
                setMessage(e.response?.data?.error || e.response?.data?.message || 'Erreur lors de l\'activation du code');
            }
        };

        redeem();
    }, [user, code]);

    // Not logged in → redirect to login with return URL (+ email d'invitation pré-rempli)
    if (!user) {
        const back = `/compte/redeem?code=${code}${invitedEmail ? `&email=${encodeURIComponent(invitedEmail)}` : ''}`;
        const qs = new URLSearchParams({ redirect: back });
        if (invitedEmail) qs.set('email', invitedEmail);
        return <Navigate to={`/connexion?${qs.toString()}`} replace />;
    }

    return (
        <div className="auth-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ maxWidth: 480, width: '100%', textAlign: 'center', padding: '40px 20px' }}>
                <ContentIllustration name="gift" size={64} style={{ color: '#7c3aed', marginBottom: 16 }} />
                <h1 style={{ fontSize: '1.5rem', marginBottom: 16 }}>Activation de code</h1>

                {status === 'no-code' && (
                    <div>
                        <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>Aucun code fourni.</p>
                        <Link to="/compte" className="btn btn-primary">Retour à mon compte</Link>
                    </div>
                )}

                {status === 'loading' && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        <Loader2 size={20} className="spin" />
                        <span>Activation du code <strong>{code}</strong>...</span>
                    </div>
                )}

                {status === 'success' && (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#10b981', marginBottom: 16 }}>
                            <CheckCircle size={24} />
                            <span style={{ fontSize: '1.1rem', fontWeight: 600 }}>Code activé !</span>
                        </div>
                        <p style={{ marginBottom: 24 }}>{message}</p>
                        <Link to="/compte/quiz/mes-thematiques" className="btn btn-primary">
                            Accéder à mes thématiques
                        </Link>
                    </div>
                )}

                {status === 'error' && (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#ef4444', marginBottom: 16 }}>
                            <AlertTriangle size={24} />
                            <span style={{ fontSize: '1.1rem', fontWeight: 600 }}>Erreur</span>
                        </div>
                        <p style={{ marginBottom: 24, color: 'var(--text-muted)' }}>{message}</p>
                        <Link to="/compte/quiz/mes-thematiques" className="btn btn-primary">
                            Retour à mes thématiques
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
